import clsx from "clsx";
import React, { useEffect } from "react";
import { Box, Button, DialogProps, Divider, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { BaseDialog } from "../dialog/BaseDialog";
import { SearchField } from "../fields/SearchField";
import {
  GroupDto2,
  UserBriefInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import { LoadMoreButton } from "../button/LoadMoreButton";
import { GroupMemberSkeleton } from "../loading/GroupMemberSkeleton";
import { useDebounce } from "../../hooks/useDebounce";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import ClientGroupsService from "../../services/ClientGroupsService";
import { COACH_GROUPS_LIST_QUERY_KEY } from "../../routes/coach/groups/CoachGroupsListRoute";
import { useOptimisticUpdateMutation } from "../../hooks/useOptimisticUpdateMutation";
import { GROUP_MEMBERS_LIST_QUERY_KEY } from "../group-members/GroupMembersList";
import { COACH_GROUP_WRAPPER_QUERY_KEY } from "../../routes/coach/group/CoachGroupRouteWrapper";
import { extractSlugId } from "../../utils/slug";
import TeamService from "../../services/TeamService";
import GroupsService from "../../services/GroupsService";
import { SelectableItem } from "../item/SelectableItem";
import {
  ClientAddableItem,
  ClientAddableItemProps,
} from "../item/ClientAddableItem";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    width: theme.spacing(65.5),
  },

  coachHeader: {
    padding: theme.spacing(2.5, 0),
  },

  coachTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    marginTop: theme.spacing(2),
  },

  clients: {
    height: 438,
    overflowY: "auto",
    paddingRight: theme.spacing(2),
  },

  clientItem: {
    padding: theme.spacing(2.5, 0),
    paddingLeft: theme.spacing(6),
  },

  button: {
    height: theme.spacing(7),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    marginTop: theme.spacing(2),
  },
}));

export interface ManageGroupHierarchyDialogProps extends DialogProps {
  group: GroupDto2;
}

export const MANAGE_GROUP_HIERARCHY_DIALOG_QUERY_KEY =
  "manage-group-hierarchy-dialog";

export const EXISTING_MEMBERS_QUERY_KEY =
  "manage-group-hierarchy-existing-members";

const PAGE_SIZE = 10;
const EXISTING_MEMBERS_PAGE_SIZE = 100;
const FETCH_NEXT_PAGE_WHEN_REMAINING = 2;

type CoachWithClients = {
  coach: UserBriefInfoDto;
  clients: UserBriefInfoDto[];
};

export function ManageGroupHierarchyDialog(
  props: ManageGroupHierarchyDialogProps,
) {
  const { className, onClose, group, ...other } = props;
  const s = useStyles();
  const queryClient = useQueryClient();

  const [filter, setFilter] = React.useState("");
  const [addedMembers, setAddedMembers] = React.useState<UserBriefInfoDto[]>(
    [],
  );
  const [existingMembersInitialized, setExistingMembersInitialized] =
    React.useState(false);

  const slugId = extractSlugId(group.slug);

  const debouncedQuery = useDebounce(filter, 300);

  // Reset query cache when dialog opens to ensure fresh data
  useEffect(() => {
    if (props.open) {
      queryClient.resetQueries({
        queryKey: [EXISTING_MEMBERS_QUERY_KEY, { groupId: group.id }],
      });
    }
  }, [props.open, group.id, queryClient]);

  const {
    data: existingMembersData,
    isLoading: isLoadingExistingMembers,
    isFetching: isFetchingExistingMembers,
    hasNextPage: hasNextExistingMembersPage,
    fetchNextPage: fetchNextExistingMembersPage,
    isFetchingNextPage: isFetchingNextExistingMembersPage,
  } = useInfiniteQuery({
    queryKey: [EXISTING_MEMBERS_QUERY_KEY, { groupId: group.id }],
    queryFn: async ({ pageParam = 1 }) => {
      const page = await GroupsService.getGroupMembers(
        group.id,
        pageParam as number,
        EXISTING_MEMBERS_PAGE_SIZE,
      );
      return {
        items: page.items.map(
          (m) =>
            ({
              id: m.id,
              email: m.email,
              displayName: m.displayName,
              photoUrl: m.photoUrl,
              username: m.username,
            }) as UserBriefInfoDto,
        ),
        hasNextPage: page.hasNextPage,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.hasNextPage ? pages.length + 1 : undefined,
    staleTime: 0,
    enabled: props.open === true,
  });

  const existingMembers = React.useMemo(
    () => existingMembersData?.pages?.flatMap((page) => page.items) ?? [],
    [existingMembersData],
  );

  // Auto-fetch all existing members pages and sync to addedMembers
  useEffect(() => {
    if (hasNextExistingMembersPage && !isFetchingNextExistingMembersPage) {
      fetchNextExistingMembersPage();
    }
  }, [
    hasNextExistingMembersPage,
    isFetchingNextExistingMembersPage,
    fetchNextExistingMembersPage,
  ]);

  useEffect(() => {
    if (
      existingMembers.length > 0 &&
      !isFetchingExistingMembers &&
      !existingMembersInitialized
    ) {
      setAddedMembers(existingMembers);
      setExistingMembersInitialized(true);
    }
  }, [existingMembers, existingMembersInitialized, isFetchingExistingMembers]);

  const {
    data,
    ref: sentinelRef,
    isLoading: isLoadingCoachesAndClients,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteScrollQuery({
    queryKey: [
      MANAGE_GROUP_HIERARCHY_DIALOG_QUERY_KEY,
      { groupId: group.id, query: debouncedQuery },
    ],
    queryFn: ({ pageParam = 1 }) =>
      TeamService.getGroupCoachesAndClients(
        pageParam as number,
        PAGE_SIZE,
        group.id,
        debouncedQuery === "" ? undefined : debouncedQuery,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.hasNextPage ? pages.length + 1 : undefined,
  });

  const isLoadingInitial =
    isLoadingExistingMembers ||
    isLoadingCoachesAndClients ||
    (hasNextExistingMembersPage && !existingMembersInitialized);

  const coachGroups = data?.pages?.flatMap((page) => page.items) ?? [];

  const handleFilterChange = React.useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >((event) => {
    setFilter(event.target.value);
  }, []);

  const handleFilterReset = React.useCallback(() => {
    setFilter("");
  }, []);

  const resetState = React.useCallback(() => {
    setFilter("");
    setAddedMembers([]);
    setExistingMembersInitialized(false);
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onClose && onClose({}, "backdropClick");
  }, [onClose, resetState]);

  const { mutate: updateGroupMembers, isPending: updateInFlight } =
    useOptimisticUpdateMutation({
      queryKey: [COACH_GROUPS_LIST_QUERY_KEY],
      mutationFn: ClientGroupsService.updateGroupMembers,
      optimisticUpdater: {
        updateFn: (oldData: any, variables: any) => {
          const groupIndex = oldData.items.findIndex(
            (g: any) => g.id === variables.id,
          );

          if (groupIndex !== -1) {
            oldData.items[groupIndex].totalMembersCount = addedMembers.length;
            oldData.items[groupIndex].members = addedMembers;
          }
          return oldData;
        },
      },
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              MANAGE_GROUP_HIERARCHY_DIALOG_QUERY_KEY,
              { groupId: group.id },
            ],
            refetchType: "inactive",
          });
          queryClient.invalidateQueries({
            queryKey: [COACH_GROUP_WRAPPER_QUERY_KEY, { slugId: slugId }],
          });
          queryClient.invalidateQueries({
            queryKey: [GROUP_MEMBERS_LIST_QUERY_KEY, { groupId: group.id }],
          });
        },
      },
    });
  const handleToggle: ClientAddableItemProps["onToggle"] = React.useCallback(
    (added, user) => {
      if (added) {
        setAddedMembers((v) => {
          if (v.some(({ id }) => id === user.id)) {
            return v;
          }
          return [...v, user];
        });
      } else {
        setAddedMembers((v) => {
          return v.filter(({ id }) => id !== user.id);
        });
      }
    },
    [setAddedMembers],
  );

  const isAdded = React.useCallback(
    (user: UserBriefInfoDto) =>
      Boolean(addedMembers.find(({ id }) => id === user.id)),
    [addedMembers],
  );

  const handleUpdate = React.useCallback(async () => {
    const sampleClientIds = addedMembers
      .filter(({ email }) => email === "sample@stridist.com")
      .map(({ id }) => id);
    const regularEmails = addedMembers
      .filter(({ email }) => email !== "sample@stridist.com")
      .map(({ email }) => email);

    updateGroupMembers({
      id: group.id,
      emails: regularEmails,
      userIds: sampleClientIds,
    });
    handleClose();
  }, [addedMembers, group.id, handleClose, updateGroupMembers]);

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title={group.name}
      closeDisabled={updateInFlight}
      onClose={handleClose}
      PaperProps={{ className: s.paper }}
      {...other}
    >
      <SearchField
        variant="outlined"
        fullWidth
        value={filter}
        onChange={handleFilterChange}
        onReset={handleFilterReset}
        placeholder="Search coaches or clients"
      />

      <Box className={s.clients}>
        {isLoadingInitial
          ? Array.from({ length: 3 }).map((_, i) => (
              <GroupMemberSkeleton key={`group-member-skeleton-${i}`} />
            ))
          : coachGroups.map(({ clients, ...coach }, groupIndex, arr) => (
              <React.Fragment key={coach.email}>
                <Typography className={s.coachTitle}>Coach</Typography>
                <SelectableItem
                  className={s.coachHeader}
                  avatarSrc={coach.photoUrl}
                  header={coach.displayName}
                  subheader={coach.email}
                  added={isAdded(coach as UserBriefInfoDto)}
                  onToggle={(added) =>
                    handleToggle(added, coach as UserBriefInfoDto)
                  }
                />
                {clients?.map((client, i, clientArr) => (
                  <React.Fragment key={client.email}>
                    {groupIndex ===
                      arr.length - FETCH_NEXT_PAGE_WHEN_REMAINING &&
                      i ===
                        clientArr.length - FETCH_NEXT_PAGE_WHEN_REMAINING && (
                        <div ref={sentinelRef} />
                      )}
                    <ClientAddableItem
                      className={s.clientItem}
                      client={client}
                      added={isAdded(client)}
                      onToggle={handleToggle}
                    />
                    {i < clientArr.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {groupIndex < arr.length - 1 && <Divider />}
              </React.Fragment>
            ))}

        {hasNextPage && (
          <LoadMoreButton
            sx={{ mb: 2 }}
            fullWidth
            onClick={() => !isFetchingNextPage && fetchNextPage()}
            disabled={isFetching && !isFetchingNextPage}
            loading={isFetchingNextPage}
          />
        )}
      </Box>

      <Divider />

      <Button
        className={s.button}
        fullWidth
        variant="contained"
        children={group.totalMembersCount > 0 ? "Save changes" : "Add members"}
        onClick={handleUpdate}
        disabled={updateInFlight}
      />
    </BaseDialog>
  );
}
